import { Plus, Receipt } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
	BudgetAlertsSection,
	CostBudgetView,
} from "@/components/blocks/budget";
import { RevisionDetailsView } from "@/components/blocks/budget/revision-details-view";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { useBudgetPermissions } from "@/hooks/use-budget-permissions";

import { fetchCostCentreBudgetsByProject } from "@/lib/data-service";
import type { BudgetRevision, CostCategory } from "@/types/budget.types";
import { useProjectDetailsContext } from "../context";

export function CostBudgetTab() {
	const ctx = useProjectDetailsContext();

	// Destructure all needed values from context
	const {
		project,
		numericProjectId, mockSeedProjectId,
		budgetData,
		budgetLoading,
		budgetError,
		setEditCostBudgetPageOpen,
		setBudgetExportModalOpen,
		setActiveBudgetType,
		costRevisions,
		showCostRevisionHistory,
		setShowCostRevisionHistory,
		setCostLoadedRevision,
		setCostMakeActiveRevision,
		setCostMakeActiveSource,
		setIsCostNewRevision,
		costCentres,
	} = ctx;

	const budgetPermissions = useBudgetPermissions();

	// In Cost Centre mode, fetch CC budget data from API.
	// Re-fetches whenever budgetData changes (e.g. after a save triggers refetchBudget).
	// Uses a cancelled-flag cleanup so that a dependency change (budgetData after save)
	// always triggers a fresh fetch — the previous in-flight request is discarded.
	const [ccBudgetEntries, setCcBudgetEntries] = useState<
		Record<number, Record<string, number>>
	>({});
	// biome-ignore lint/correctness/useExhaustiveDependencies: budgetData is intentionally included to trigger re-fetch after save
	useEffect(() => {
		if (!budgetPermissions.isCostCentreMode || !costCentres?.length) return;
		let cancelled = false;
		fetchCostCentreBudgetsByProject(numericProjectId, "cost")
			.then((entries) => {
				if (cancelled) return;
				const map: Record<number, Record<string, number>> = {};
				for (const entry of entries) {
					if (entry.monthlyBudgets) {
						const monthly =
							typeof entry.monthlyBudgets === "string"
								? (JSON.parse(entry.monthlyBudgets) as Record<string, number>)
								: entry.monthlyBudgets;
						map[entry.costCentreId] = monthly;
					}
				}
				setCcBudgetEntries(map);
			})
			.catch(() => {
				/* fall back to empty */
			});
		return () => {
			cancelled = true;
		};
	}, [
		budgetPermissions.isCostCentreMode,
		costCentres,
		numericProjectId,
		budgetData,
	]);

	const ccModeData = useMemo(() => {
		if (!budgetPermissions.isCostCentreMode || !costCentres?.length)
			return null;
		const categories: CostCategory[] = costCentres.map((cc) => {
			const monthly = ccBudgetEntries[cc.id ?? 0];
			// Monthly phasing is the authoritative budget once entered; before that
			// fall back to the cost centre's own CostsBudget.
			const budget = monthly
				? Object.values(monthly).reduce((s, v) => s + (v ?? 0), 0)
				: (cc.costsBudget ?? 0);
			// usp_CostCentre_GetStructureByProjectId already attributes WIPLedger
			// costs to each cost centre via ExternalCostCentreCode, so actuals come
			// straight off the API rather than being assumed to be zero.
			const actual = cc.actualCosts ?? 0;
			const committed = cc.committedCosts ?? 0;
			return {
				id: cc.id ?? 0,
				name: `${cc.code} – ${cc.name}`,
				type: "labour" as const,
				budget,
				actual,
				committed,
				available: budget - actual - committed,
				progressPercent: budget > 0 ? (actual / budget) * 100 : 0,
			};
		});
		const totalBudget = categories.reduce((s, c) => s + c.budget, 0);
		const totalActual = categories.reduce((s, c) => s + c.actual, 0);
		const totalCommitted = categories.reduce((s, c) => s + c.committed, 0);
		const storedTotals = budgetData?.costBudget?.totals;
		// A cost-centre roll-up only covers the project once every cost carries a
		// mapped cost centre code. Until then the stored CostBudget header totals
		// are the authoritative project figures (they are what the project list
		// shows), so fall back to them rather than reporting 0.
		const budget = totalBudget || (storedTotals?.budget ?? 0);
		const actual = totalActual || (storedTotals?.actual ?? 0);
		const committed = totalCommitted || (storedTotals?.committed ?? 0);
		return {
			costBudget: {
				...(budgetData?.costBudget ?? {
					projectId: numericProjectId,
					phases: [],
				}),
				categories,
				totals: {
					budget,
					actual,
					committed,
					available: budget - actual - committed,
					progressPercent: budget > 0 ? (actual / budget) * 100 : 0,
				},
			},
		};
	}, [
		budgetPermissions.isCostCentreMode,
		costCentres,
		numericProjectId,
		budgetData?.costBudget,
		ccBudgetEntries,
	]);

	const costBudgetData = budgetPermissions.isCostCentreMode
		? ccModeData?.costBudget
		: budgetData?.costBudget;

	return (
		<>
			{budgetLoading ? (
				<div className="flex items-center justify-center h-96">
					<div className="text-center">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
						<p className="text-muted-foreground">Loading budget data...</p>
					</div>
				</div>
			) : budgetError ? (
				<div className="flex items-center justify-center h-96">
					<div className="text-center space-y-4">
						<div className="text-destructive mb-2">
							Error loading budget data
						</div>
						<p className="text-sm text-muted-foreground">{budgetError}</p>
					</div>
				</div>
			) : budgetData ? (
				<>
					<CostBudgetView
						projectId={mockSeedProjectId}
						resourceBudget={budgetData.resourceBudget}
						data={costBudgetData}
						onEdit={() => {
							setIsCostNewRevision(false);
							setEditCostBudgetPageOpen(true);
						}}
						onExport={() => {
							setActiveBudgetType("cost");
							setBudgetExportModalOpen(true);
						}}
						// Revision History hidden for first release — omit onHistory so the
						// button no longer renders. Sheet/state/handlers kept for re-enablement.
						// onHistory={() => {
						// 	setShowCostRevisionHistory(true);
						// }}
					/>

					{budgetData.alerts && budgetData.alerts.length > 0 && (
						<BudgetAlertsSection alerts={budgetData.alerts} />
					)}
				</>
			) : (
				<div className="flex items-center justify-center min-h-[500px]">
					<Card className="w-full max-w-md border-dashed">
						<CardContent className="flex flex-col items-center justify-center text-center p-12">
							<div className="rounded-full bg-muted p-4 mb-4">
								<Receipt className="h-8 w-8 text-muted-foreground" />
							</div>
							<h3 className="text-lg font-semibold mb-2">No Cost Budget</h3>
							<p className="text-sm text-muted-foreground mb-6 max-w-sm">
								Track expenses, commitments, and cost categories by creating a
								cost budget for this project.
							</p>
							<Button onClick={() => setEditCostBudgetPageOpen(true)}>
								<Plus className="mr-2 h-4 w-4" />
								Create Cost Budget
							</Button>
						</CardContent>
					</Card>
				</div>
			)}

			{/* Cost Budget Revision History Sheet — rendered outside the
                      loading/error/data conditional so it stays mounted across
                      budget refetch cycles and Radix Dialog state is preserved. */}
			<Sheet
				open={showCostRevisionHistory}
				onOpenChange={setShowCostRevisionHistory}
			>
				<SheetContent
					side="right"
					className="w-full sm:max-w-4xl overflow-y-auto"
				>
					<SheetHeader className="px-6 pt-6">
						<SheetTitle>Cost Budget Revision History</SheetTitle>
						<SheetDescription>
							View, compare and restore previous versions of the cost budget.
						</SheetDescription>
					</SheetHeader>
					<div className="mt-6 px-6 pb-6">
						<RevisionDetailsView
							budgetLayer="cost"
							revisions={costRevisions}
							projectName={project?.name ?? ""}
							onViewRevision={(id) => {
								const rev = costRevisions.find(
									(r: BudgetRevision) =>
										r.id === Number(id) || r.revisionNumber === Number(id),
								);
								if (rev) {
									setCostLoadedRevision(rev);
									setIsCostNewRevision(false);
									setEditCostBudgetPageOpen(true);
								}
								setShowCostRevisionHistory(false);
							}}
							onRestore={(id) => {
								const rev = costRevisions.find(
									(r: BudgetRevision) => r.id === Number(id),
								);
								if (rev) {
									setCostMakeActiveSource("restore");
									setCostMakeActiveRevision(rev);
								}
							}}
							onMakeActive={(id) => {
								const rev = costRevisions.find(
									(r: BudgetRevision) => r.id === Number(id),
								);
								if (rev) {
									setCostMakeActiveSource("make-active");
									setCostMakeActiveRevision(rev);
								}
							}}
							onNewRevision={() => {
								setShowCostRevisionHistory(false);
								setCostLoadedRevision(null);
								setIsCostNewRevision(true);
								setEditCostBudgetPageOpen(true);
							}}
							onDuplicate={(id) => {
								const rev = costRevisions.find(
									(r: BudgetRevision) =>
										r.id === Number(id) || r.revisionNumber === Number(id),
								);
								if (rev) {
									setShowCostRevisionHistory(false);
									setCostLoadedRevision(rev);
									setIsCostNewRevision(false);
									setEditCostBudgetPageOpen(true);
								}
							}}
						/>
					</div>
				</SheetContent>
			</Sheet>
		</>
	);
}
